import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import type { ExplainItem } from '../explain-table/explain-table.model';
import { ExplainTableComponent } from '../explain-table/explain-table.component';
import { buildAlignedFormula, buildPluggedInFormula } from '../measures/measures-formula.util';
import { MEASURE_DEFS, type MeasureId } from '../measures/measures.model';
import { computeAllMeasures } from '../measures/measures.util';
import { fmt } from '../chart-shared';
import { BoxPlotChartComponent } from './box-plot-chart.component';
import { BOX_PLOT_ELEMENT_DEFS, type BoxPlotElementId, type BoxPlotParams } from './box-plot-chart.model';
import { computeBoxPlots, type BoxPlotSummary } from './box-plot-chart.util';

// ---------------------------------------------------------------------------
// Box-plot element → value string
// ---------------------------------------------------------------------------

function getElementValue(s: BoxPlotSummary, id: BoxPlotElementId): string {
  switch (id) {
    case 'minimum':
      return fmt(s.min);
    case 'lower-whisker':
      return `${fmt(s.min)} to ${fmt(s.q1)}`;
    case 'q1':
      return fmt(s.q1);
    case 'iqr-box':
      return fmt(s.iqr);
    case 'median':
      return fmt(s.median);
    case 'q3':
      return fmt(s.q3);
    case 'upper-whisker':
      return `${fmt(s.q3)} to ${fmt(s.max)}`;
    case 'maximum':
      return fmt(s.max);
    case 'outliers':
      return s.outliers.length === 0 ? 'None' : s.outliers.map(fmt).join(', ');
    case 'mean':
      return fmt(s.mean);
  }
}

// ---------------------------------------------------------------------------
// Elements that can show a formula derivation
// ---------------------------------------------------------------------------

const FORMULA_MEASURE_MAP: Partial<Record<BoxPlotElementId, MeasureId>> = {
  q1: 'q1',
  median: 'median',
  q3: 'q3',
  'iqr-box': 'iqr',
  mean: 'mean',
};

function buildFormulaForElement(
  elementId: BoxPlotElementId,
  sortedData: number[],
  measures: ReadonlyMap<MeasureId, number | number[]>,
  summary: BoxPlotSummary,
): string | undefined {
  // Elements with a direct measure formula
  const measureId = FORMULA_MEASURE_MAP[elementId];
  if (measureId) {
    const def = MEASURE_DEFS.find((d) => d.id === measureId);
    if (!def) {
      return undefined;
    }
    const pluggedIn = buildPluggedInFormula(measureId, sortedData, measures);
    if (!pluggedIn) {
      return undefined;
    }
    return buildAlignedFormula(def.formulaLatex, pluggedIn);
  }

  // Custom formulas for box-plot-specific elements
  const { q1, q3, iqr, min, max, outliers } = summary;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  switch (elementId) {
    case 'minimum':
      return [
        `$$\\text{Lower fence} = Q_1 - 1.5 \\cdot \\text{IQR} = ${fmt(q1)} - 1.5 \\times ${fmt(iqr)} = ${fmt(
          lowerFence,
        )}$$`,
        `$$\\text{Minimum} = \\text{smallest } x_i \\geq ${fmt(lowerFence)} = ${fmt(min)}$$`,
      ].join('\n\n');

    case 'maximum':
      return [
        `$$\\text{Upper fence} = Q_3 + 1.5 \\cdot \\text{IQR} = ${fmt(q3)} + 1.5 \\times ${fmt(iqr)} = ${fmt(
          upperFence,
        )}$$`,
        `$$\\text{Maximum} = \\text{largest } x_i \\leq ${fmt(upperFence)} = ${fmt(max)}$$`,
      ].join('\n\n');

    case 'lower-whisker':
      return [
        `$$\\text{Lower fence} = Q_1 - 1.5 \\cdot \\text{IQR} = ${fmt(q1)} - 1.5 \\times ${fmt(iqr)} = ${fmt(
          lowerFence,
        )}$$`,
        `$$\\text{Lower whisker: } ${fmt(min)} \\text{ to } ${fmt(q1)}$$`,
      ].join('\n\n');

    case 'upper-whisker':
      return [
        `$$\\text{Upper fence} = Q_3 + 1.5 \\cdot \\text{IQR} = ${fmt(q3)} + 1.5 \\times ${fmt(iqr)} = ${fmt(
          upperFence,
        )}$$`,
        `$$\\text{Upper whisker: } ${fmt(q3)} \\text{ to } ${fmt(max)}$$`,
      ].join('\n\n');

    case 'outliers':
      if (outliers.length === 0) {
        return `$$\\text{No values } < ${fmt(lowerFence)} \\text{ or } > ${fmt(upperFence)}$$`;
      }
      return [
        `$$\\text{Lower fence} = ${fmt(lowerFence)}, \\quad \\text{Upper fence} = ${fmt(upperFence)}$$`,
        `$$\\text{Outliers: } ${outliers.map(fmt).join(',\\, ')} \\quad (\\text{outside fences})$$`,
      ].join('\n\n');

    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-box-plot-explain',
  standalone: true,
  imports: [BoxPlotChartComponent, ExplainTableComponent],
  template: `
    <dojo-box-plot-chart
      [params]="params()"
      [explainMode]="explain()"
      [showData]="showData()"
      [highlightedElement]="highlightedElement()"
    />
    @if (explain() && explainItems().length > 0) {
      <dojo-explain-table [items]="explainItems()" [(highlightedId)]="highlightedId" />
    }
  `,
  host: { class: 'block w-full h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoxPlotExplainComponent {
  readonly params = input<BoxPlotParams>({ columns: [] });
  readonly explain = input<boolean>(false);
  readonly showData = input<boolean>(false);

  readonly highlightedId = signal<string | null>(null);

  /** Typed cast for the chart component's input. */
  readonly highlightedElement = computed(() => this.highlightedId() as BoxPlotElementId | null);

  private readonly computedBoxPlots = computed(() => computeBoxPlots(this.params().columns));

  private readonly sortedData = computed<number[]>(() => {
    const columns = this.params().columns;
    if (columns.length === 0) {
      return [];
    }
    return [...columns[0].data].sort((a, b) => a - b);
  });

  private readonly measures = computed(() => {
    const sorted = this.sortedData();
    if (sorted.length === 0) {
      return new Map();
    }
    return computeAllMeasures(sorted);
  });

  readonly explainItems = computed<ExplainItem[]>(() => {
    const summaries = this.computedBoxPlots().summaries;
    if (summaries.length === 0) {
      return [];
    }
    const s = summaries[0];
    const sorted = this.sortedData();
    const measures = this.measures();

    return BOX_PLOT_ELEMENT_DEFS.map((def) => ({
      id: def.id,
      label: def.label,
      description: def.description,
      color: def.color,
      value: getElementValue(s, def.id),
      formulaLatex: sorted.length > 0 ? buildFormulaForElement(def.id, sorted, measures, s) : undefined,
    }));
  });
}
